import { api, formatUrl } from '../api';
import pickBy from 'lodash/pickBy';
import {
  IRateContentPayload,
  IAnswerContentQuizPayload,
  IEduContent,
  IEduContentDetails,
  IDentistContentAnalytics,
  IAssignContentsToDentistPayload,
  IEarnedCPDPointsRes,
  TEduContentTag,
} from './types/common';

export const EDU_API_URLS = {
  BASE: '/education-contents',
  ANALYTICS: '/analytics/education-contents',
  VIEW_STATUS: '/education-contents/view-status',
};
export interface IGetContentListParams {
  perPage: string;
  page?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'decs';
  tags?: string;
  status?: string;
  difficultyLevel?: string;
  contentType?: string;
  case?: string;
  q?: string;
  category?: string;
}

export const eduApi = api.injectEndpoints({
  endpoints: (build) => ({
    getContentList: build.query<
      { total: number; contents: IEduContent[] | { [key: string]: IEduContent[] } },
      Partial<IGetContentListParams>
    >({
      query: (params) => formatUrl(`${EDU_API_URLS.BASE}?${new URLSearchParams(pickBy(params))}`),
      providesTags: (result, _error, args) => {
        if (result) {
          // generate tags by id
          const tags: TEduContentTag[] = Array.isArray(result.contents)
            ? result?.contents.map(({ id }: { id: string }) => ({ type: 'EduContent', id }))
            : Object.entries(result?.contents).reduce(
                (out: TEduContentTag[], [_, items]) =>
                  out.concat(items.map((item) => ({ type: 'EduContent', id: item.id }))),
                [],
              );
          // add id LIST if have bookmarked arg and invalid it if have new bookmark because
          // e.g load bookmarked data then bookmark new content,
          // the id of new content is not in previous bookmarked data so the list will not be refresh
          return args.status === 'bookmarked' ? [{ type: 'EduContent', id: 'LIST' }] : [...tags];
        }
        return [{ type: 'EduContent', id: 'LIST' }];
      },
    }),
    getContentById: build.query<IEduContentDetails, { contentId: string; from?: string | null }>({
      query: ({ contentId, from }) =>
        formatUrl(`${EDU_API_URLS.BASE}/${contentId}?${new URLSearchParams(pickBy({ from: from! }))}`),
    }),
    getDentistContentAnalytics: build.query<IDentistContentAnalytics, unknown>({
      query: () => formatUrl(`${EDU_API_URLS.ANALYTICS}`),
    }),
    getNotionData: build.query<any, string>({
      query: (pageId) => `https://notion-api-32co.herokuapp.com/notion/${pageId}`,
    }),
    markAsRead: build.mutation<void, string>({
      query: (contentId) => ({
        url: formatUrl(`${EDU_API_URLS.BASE}/${contentId}/view`),
        method: 'PUT',
      }),
    }),
    rateContent: build.mutation<unknown, { contentId: string; payload: IRateContentPayload }>({
      query: ({ contentId, payload }) => ({
        url: formatUrl(`${EDU_API_URLS.BASE}/${contentId}/ratings`),
        method: 'POST',
        body: payload,
      }),
    }),
    answerContentQuiz: build.mutation<unknown, { contentId: string; payload: IAnswerContentQuizPayload }>({
      query: ({ contentId, payload }) => ({
        url: formatUrl(`${EDU_API_URLS.BASE}/${contentId}/answers`),
        method: 'POST',
        body: payload,
      }),
    }),
    assignContentsToDentist: build.mutation<unknown, IAssignContentsToDentistPayload>({
      query: (payload) => ({
        url: formatUrl(`${EDU_API_URLS.BASE}/assign`),
        method: 'POST',
        body: payload,
      }),
    }),
    bookmarkDentistContent: build.mutation<unknown, string>({
      query: (contentId) => ({
        url: formatUrl(`${EDU_API_URLS.BASE}/${contentId}/bookmarks`),
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'EduContent', id },
        { type: 'EduContent', id: 'LIST' },
      ],
    }),
    getEarnedCPDPoints: build.query<IEarnedCPDPointsRes, unknown>({
      query: () => formatUrl(`${EDU_API_URLS.BASE}/earned`),
    }),
    getKeywordTags: build.query<{ tags: string[] }, null>({
      query: () => formatUrl(`${EDU_API_URLS.BASE}/tags`),
    }),
    viewStatus: build.mutation<void, void>({
      query: () => ({
        url: formatUrl(`${EDU_API_URLS.BASE}/view-status`),
        method: 'PUT',
      }),
    }),
  }),
});

export const {
  useGetContentListQuery,
  useGetContentByIdQuery,
  useGetDentistContentAnalyticsQuery,
  useGetNotionDataQuery,
  useMarkAsReadMutation,
  useRateContentMutation,
  useAnswerContentQuizMutation,
  useAssignContentsToDentistMutation,
  useBookmarkDentistContentMutation,
  useGetEarnedCPDPointsQuery,
  useGetKeywordTagsQuery,
  useViewStatusMutation,
} = eduApi;
